import { createElement } from 'react';
import { captureException } from '@sentry/react';
import { BiDuplicate } from 'react-icons/bi';
import { TbArchive, TbArchiveOff, TbLock, TbLockOpen, TbStarFilled, TbStarOff } from 'react-icons/tb';
import { StateCreator } from 'zustand';

import { queryClient } from '@/clients';
import {
    fetchPrefetchedDetailQuery,
    getPrefetchedDetailQueryData,
    workspaceResourceDetailQueryOptions,
    workspaceResourceQueryKeys,
} from '@/queries/tanstack/prefetch';
import { investmentService, type InvestmentCollaborator } from '@/services';
import { toast } from '@/shared/components/toasts/PlatformToast';
import {
    appendEntity,
    createSingleFlightKey,
    mergeEntity,
    mergeEntityById,
    removeEntityById,
    replaceEntityById,
    replaceOptimisticEntityById,
    runSingleFlight,
    upsertEntityById,
} from '@/stores/helpers/collections';
import { reduceCachedEntityDelta, reduceEntityDelta } from '@/stores/helpers/delta';
import { publishLocalEntityDelta } from '@/stores/realtime';
import { Investment } from '@/types';

import { InvestmentActions, InvestmentState } from '../types';

const cloneInvestment = (investment: Investment) => structuredClone(investment);
const cloneInvestments = (investments: Investment[]) => structuredClone(investments);

const publishInvestmentDelta = (
    operation: 'create' | 'update' | 'delete',
    entityId: string,
    payload?: Partial<Investment>,
) =>
    publishLocalEntityDelta<'investment', Partial<Investment>>({
        entity: 'investment',
        operation,
        entityId,
        ...(payload ? { payload } : {}),
    });

export const createInvestmentActions: StateCreator<
    InvestmentState & InvestmentActions,
    [['zustand/immer', never]],
    [],
    InvestmentActions
> = (set, get) => ({
    // Core
    fetchInvestments: async (params) => {
        set({ loading: true });

        try {
            const investments = await investmentService.list(params);

            set({ investments, loading: false, error: undefined });
        } catch (error) {
            captureException(error);

            set({ loading: false, error: 'Failed to load investments' });
        }
    },
    fetchInvestmentById: async (id) => {
        const queryOptions = workspaceResourceDetailQueryOptions.investments(id);
        const cachedInvestment = getPrefetchedDetailQueryData(queryClient, queryOptions);

        set({
            loadingInvestment: !cachedInvestment,
            error: undefined,
            ...(cachedInvestment ? { selectedInvestment: cachedInvestment } : {}),
        });

        try {
            const investment = await fetchPrefetchedDetailQuery(queryClient, queryOptions);

            set({ selectedInvestment: investment, loadingInvestment: false });
        } catch (error) {
            captureException(error);

            set((state) => {
                state.error = 'Failed to fetch investment detail';
                state.loadingInvestment = false;
            });
        }
    },
    clearSelectedInvestment: () => {
        set((state) => {
            state.selectedInvestment = null;
        });
    },
    createInvestment: async (input) =>
        runSingleFlight(createSingleFlightKey('investments:create', input), async () => {
            const tempId = `temp-${Date.now()}`;
            const optimistic = { id: tempId, ...input } as Investment;

            set({ investments: appendEntity(get().investments, optimistic) });

            publishInvestmentDelta('create', tempId, optimistic);

            try {
                const investment = await investmentService.create(input);

                set({
                    investments: replaceOptimisticEntityById(get().investments, tempId, investment),
                    selectedInvestment:
                        get().selectedInvestment?.id === tempId || get().selectedInvestment?.id === investment.id
                            ? investment
                            : get().selectedInvestment,
                });

                publishInvestmentDelta('delete', tempId);
                publishInvestmentDelta('create', investment.id, investment);

                return investment;
            } catch (error) {
                captureException(error);

                set({ investments: removeEntityById(get().investments, tempId), error: 'Failed to create investment' });

                publishInvestmentDelta('delete', tempId);
            }
        }),
    updateInvestment: async (id, updates) => {
        const prev = get().investments.find((f) => f.id === id);
        if (!prev) {
            return;
        }
        const prevSnapshot = cloneInvestment(prev);

        set({
            investments: mergeEntityById(get().investments, id, updates),
            selectedInvestment:
                get().selectedInvestment?.id === id
                    ? mergeEntity(get().selectedInvestment!, updates)
                    : get().selectedInvestment,
        });

        publishInvestmentDelta('update', id, updates);

        try {
            const investment = await investmentService.update(id, updates);

            set({
                investments: upsertEntityById(
                    get().investments.filter((f) => f.id === id || f.id !== investment.id),
                    investment,
                ),
                selectedInvestment:
                    get().selectedInvestment?.id === id
                        ? mergeEntity(get().selectedInvestment!, investment)
                        : get().selectedInvestment,
            });

            publishInvestmentDelta('update', investment.id ?? id, investment);
        } catch (error) {
            captureException(error);

            set({
                investments: replaceEntityById(get().investments, id, prevSnapshot),
                selectedInvestment: get().selectedInvestment?.id === id ? prevSnapshot : get().selectedInvestment,
                error: 'Failed to update investment',
            });

            publishInvestmentDelta('update', id, prevSnapshot);
        }
    },
    deleteInvestment: async (id) => {
        const prev = cloneInvestments(get().investments);
        const selectedInvestment = get().selectedInvestment;
        const prevSelected = selectedInvestment ? cloneInvestment(selectedInvestment) : null;
        const deleted = prev.find((investment) => investment.id === id);

        set((state) => {
            state.investments = state.investments.filter((f) => f.id !== id);

            // Clear selectedInvestment if it's the deleted one
            if (state.selectedInvestment?.id === id) {
                state.selectedInvestment = null;
            }
        });

        publishInvestmentDelta('delete', id);

        try {
            await investmentService.delete(id);
        } catch (error) {
            captureException(error);

            set({
                investments: prev,
                selectedInvestment: prevSelected?.id === id ? prevSelected : get().selectedInvestment,
                error: 'Failed to delete investment',
            });

            if (deleted) {
                publishInvestmentDelta('create', id, deleted);
            }
        }
    },
    applyInvestmentDelta: (packet) => {
        if (packet.entity !== 'investment') {
            return;
        }

        const next = reduceEntityDelta<Investment>(get().investments, get().selectedInvestment, packet);

        if (!next) {
            return;
        }

        if (packet.operation === 'delete') {
            queryClient.removeQueries({ queryKey: workspaceResourceQueryKeys.investments(packet.entityId) });
        } else if (packet.payload) {
            queryClient.setQueryData(
                workspaceResourceQueryKeys.investments(packet.entityId),
                (existing: Investment | undefined) => reduceCachedEntityDelta(existing, packet),
            );
        }

        set({
            investments: next.items,
            selectedInvestment: next.selected,
        });
    },
    // - Aliases
    duplicateInvestment: async (id) => {
        const prev = cloneInvestments(get().investments);

        try {
            const duplicated = await investmentService.duplicate(id);

            set({
                investments: upsertEntityById(get().investments, duplicated, { prepend: true }),
            });

            publishInvestmentDelta('create', duplicated.id, duplicated);

            toast(`Investment Duplicated`, {
                icon: createElement(BiDuplicate, { size: '1rem' }),
                description: duplicated.name,
                resource: { id: duplicated.id, type: 'investment' },
            });

            return duplicated;
        } catch (error) {
            captureException(error);

            set({ investments: prev, error: 'Failed to duplicate investment' });

            toast.error('Failed to duplicate investment');
        }
    },
    toggleInvestmentFavorite: async (id, isFavorite) => {
        const prev = get().investments.find((f) => f.id === id);

        if (!prev) {
            return;
        }
        const prevSnapshot = cloneInvestment(prev);

        set((state) => {
            const investment = state.investments.find((f) => f.id === id);
            if (investment) {
                investment.isFavorite = isFavorite;
            }

            // Also update selectedInvestment if it matches
            if (state.selectedInvestment?.id === id) {
                state.selectedInvestment.isFavorite = isFavorite;
            }
        });

        try {
            await investmentService.setFavorite(id, isFavorite);

            toast(`${isFavorite ? 'Added to' : 'Removed from'} Favorites`, {
                icon: createElement(isFavorite ? TbStarFilled : TbStarOff, { size: '1rem' }),
                description: prev.name,
                resource: { id, type: 'investment' },
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                const investment = state.investments.find((f) => f.id === id);
                if (investment) {
                    investment.isFavorite = prevSnapshot.isFavorite;
                }

                // Revert selectedInvestment isFavorite if it matches
                if (state.selectedInvestment?.id === id) {
                    state.selectedInvestment.isFavorite = prevSnapshot.isFavorite;
                }

                state.error = 'Failed to update favorite';
            });
        }
    },
    toggleInvestmentLock: async (id, isLocked) => {
        const prev = get().investments.find((f) => f.id === id);

        if (!prev) {
            return;
        }
        const prevSnapshot = cloneInvestment(prev);

        set((state) => {
            const investment = state.investments.find((f) => f.id === id);
            if (investment) {
                investment.isLocked = isLocked;
            }

            // Also update selectedInvestment if it matches
            if (state.selectedInvestment?.id === id) {
                state.selectedInvestment.isLocked = isLocked;
            }
        });

        publishInvestmentDelta('update', id, { isLocked });

        try {
            await investmentService.setLocked(id, isLocked);

            toast(`Investment ${isLocked ? 'Locked' : 'Unlocked'}`, {
                icon: createElement(isLocked ? TbLock : TbLockOpen, { size: '1rem' }),
                description: prev.name,
                resource: { id, type: 'investment' },
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                const investment = state.investments.find((f) => f.id === id);
                if (investment) {
                    investment.isLocked = prevSnapshot.isLocked;
                }

                // Revert selectedInvestment isLocked if it matches
                if (state.selectedInvestment?.id === id) {
                    state.selectedInvestment.isLocked = prevSnapshot.isLocked;
                }

                state.error = 'Failed to update favorite';
            });

            publishInvestmentDelta('update', id, { isLocked: prevSnapshot.isLocked });
        }
    },
    toggleInvestmentArchive: async (id, isArchived) => {
        const prev = get().investments.find((f) => f.id === id);

        if (!prev) {
            return;
        }
        const prevSnapshot = cloneInvestment(prev);

        set((state) => {
            const investment = state.investments.find((f) => f.id === id);
            if (investment) {
                investment.isArchived = isArchived;
            }

            // Also update selectedInvestment if it matches
            if (state.selectedInvestment?.id === id) {
                state.selectedInvestment.isArchived = isArchived;
            }
        });

        publishInvestmentDelta('update', id, { isArchived });

        try {
            await investmentService.setArchived(id, isArchived);

            toast(`Investment ${isArchived ? 'Archived' : 'Unarchived'}`, {
                icon: createElement(isArchived ? TbArchive : TbArchiveOff, { size: '1rem' }),
                description: prev.name,
                resource: { id, type: 'investment' },
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                const investment = state.investments.find((f) => f.id === id);
                if (investment) {
                    investment.isArchived = prevSnapshot.isArchived;
                }

                // Revert selectedInvestment isArchived if it matches
                if (state.selectedInvestment?.id === id) {
                    state.selectedInvestment.isArchived = prevSnapshot.isArchived;
                }

                state.error = 'Failed to update favorite';
            });

            publishInvestmentDelta('update', id, { isArchived: prevSnapshot.isArchived });
        }
    },
    // Collaborators
    fetchInvestmentCollaborators: async (investmentId) => {
        set((state) => {
            state.loadingInvestmentCollaborators = true;
            state.error = undefined;
        });

        try {
            const collaborators = await investmentService.getCollaborators(investmentId);

            set((state) => {
                state.investmentCollaborators = collaborators;
                state.loadingInvestmentCollaborators = false;
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                state.error = 'Failed to fetch investment detail';
                state.loadingInvestmentCollaborators = false;
            });
        }
    },
    clearInvestmentCollaborators: () => {
        set((state) => {
            state.investmentCollaborators = [];
        });
    },
    createInvestmentCollaborator: async (investmentId, input) =>
        runSingleFlight(createSingleFlightKey(`investments:${investmentId}:collaborators:create`, input), async () => {
            const tempId = `temp-${Date.now()}`;

            const optimistic = { id: tempId, ...input } as InvestmentCollaborator;

            set({ investmentCollaborators: appendEntity(get().investmentCollaborators, optimistic) });

            try {
                const collaborator = await investmentService.createCollaborator(investmentId, input);

                set({
                    investmentCollaborators: replaceOptimisticEntityById(
                        get().investmentCollaborators,
                        tempId,
                        collaborator,
                    ),
                });

                return collaborator;
            } catch (error) {
                captureException(error);

                set({
                    investmentCollaborators: removeEntityById(get().investmentCollaborators, tempId),
                    error: 'Failed to create investment collaborator',
                });
            }
        }),
    updateInvestmentCollaborator: async (investmentId, id, updates) => {
        const prev = get().investmentCollaborators.find((f) => f.id === id);

        if (!prev) {
            return;
        }

        set({ investmentCollaborators: mergeEntityById(get().investmentCollaborators, id, updates) });

        try {
            const collaborator = await investmentService.updateCollaborator(investmentId, id, updates);

            set({ investmentCollaborators: mergeEntityById(get().investmentCollaborators, id, collaborator) });
        } catch (error) {
            captureException(error);

            set({
                investmentCollaborators: replaceEntityById(get().investmentCollaborators, id, prev),
                error: 'Failed to update investment collaborator',
            });
        }
    },
    deleteInvestmentCollaborator: async (investmentId, id) => {
        const prev = get().investmentCollaborators;

        set({ investmentCollaborators: removeEntityById(get().investmentCollaborators, id) });

        try {
            await investmentService.deleteCollaborator(investmentId, id);
        } catch (error) {
            captureException(error);

            set({ investmentCollaborators: prev, error: 'Failed to delete investment' });
        }
    },
});
